import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  canProject,
  type Deck,
  type DeckDraft,
  type DeckVersion,
  type TrainingSession,
} from '@allemnii-pulse/shared';
import { useProject } from '../projects/ProjectProvider.js';
import { useDocument } from '../../lib/firestore.js';
import { api, ApiError } from '../../lib/api.js';
import { relativeTime } from '../../lib/time.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { SlideEditor } from './editor/SlideEditor.js';
import { deckImagePaths, uploadSlideImage, useSlideImageUrls } from './SlideView.js';

const AUTOSAVE_MS = 1500;

/**
 * Hosts the slide editor for one session. Edits autosave to a draft (deckDraft/current) a moment
 * after each change; "Save version" writes them as the next deck version and clears the draft.
 * Re-opening the editor resumes the draft when it was based on the current version, and offers
 * it — without loading it by default — when someone has saved a newer version since.
 */
export function SlidesEditorPage() {
  const { projectId, role } = useProject();
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const canManage = canProject(role, 'training.manage');
  const sessionPath = `projects/${projectId}/trainingSessions/${sessionId}`;
  const { data: session } = useDocument<TrainingSession>(sessionId ? sessionPath : null);
  const { data: version } = useDocument<DeckVersion>(
    session?.currentDeckVersionId
      ? `${sessionPath}/deckVersions/${session.currentDeckVersionId}`
      : null,
  );
  const { data: draft, loading: draftLoading } = useDocument<DeckDraft>(
    sessionId ? `${sessionPath}/deckDraft/current` : null,
  );

  const [deck, setDeck] = useState<Deck | null>(null);
  const [baseVersionId, setBaseVersionId] = useState<string | null>(null);
  const [staleDraft, setStaleDraft] = useState<DeckDraft | null>(null);
  // Bumped whenever a different deck is swapped in from outside the editor, so it remounts with
  // a fresh undo history instead of letting undo step back across the swap.
  const [editorKey, setEditorKey] = useState(0);
  const [autosave, setAutosave] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>(
    'idle',
  );
  const [summary, setSummary] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<Deck | null>(null);

  // Load once, when both the current version and the draft (if any) have arrived.
  useEffect(() => {
    if (deck || !version || draftLoading) return;
    setBaseVersionId(version.id);
    if (draft && draft.baseVersionId === version.id) {
      setDeck(draft.deck);
      setAutosave('saved');
    } else {
      setDeck(version.deck);
      if (draft) setStaleDraft(draft);
    }
  }, [deck, version, draft, draftLoading]);

  const flush = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const next = pending.current;
    if (!next || !sessionId) return;
    pending.current = null;
    setAutosave('saving');
    try {
      await api.put(`/projects/${projectId}/training/sessions/${sessionId}/deck/draft`, {
        deck: next,
        baseVersionId,
      });
      setAutosave((s) => (s === 'saving' ? 'saved' : s));
    } catch {
      setAutosave('error');
    }
  }, [projectId, sessionId, baseVersionId]);

  // Don't lose the last few keystrokes when leaving the page.
  useEffect(() => () => void flush(), [flush]);

  function handleChange(next: Deck) {
    setDeck(next);
    pending.current = next;
    setAutosave('pending');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), AUTOSAVE_MS);
  }

  async function saveVersion() {
    if (!deck || !sessionId) return;
    if (timer.current) clearTimeout(timer.current);
    pending.current = null;
    setSaving(true);
    setError(null);
    try {
      await api.post(`/projects/${projectId}/training/sessions/${sessionId}/deck/versions`, {
        deck,
        baseVersionId,
        ...(summary.trim() ? { changeSummary: summary.trim() } : {}),
      });
      navigate(`/projects/${projectId}/sessions/${sessionId}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the slides.');
      setSaving(false);
    }
  }

  async function discard() {
    if (!window.confirm('Discard all unsaved changes to these slides?')) return;
    if (timer.current) clearTimeout(timer.current);
    pending.current = null;
    await api
      .delete(`/projects/${projectId}/training/sessions/${sessionId}/deck/draft`)
      .catch(() => undefined);
    navigate(`/projects/${projectId}/sessions/${sessionId}`);
  }

  const imageUrls = useSlideImageUrls(projectId, deckImagePaths(deck));
  const uploadImage = useCallback((file: File) => uploadSlideImage(projectId, file), [projectId]);

  if (!canManage)
    return <p className="p-6 text-sm text-muted">Only project leads can edit slides.</p>;
  if (session && !session.currentDeckVersionId)
    return <p className="p-6 text-sm text-muted">This session has no slides yet.</p>;
  if (session?.status === 'generating')
    return (
      <p className="p-6 text-sm text-muted">
        Slides are being regenerated — try again in a minute.
      </p>
    );
  if (!deck || !session) return <p className="p-6 text-sm text-muted">Loading…</p>;

  const dirty = autosave !== 'idle';

  return (
    // Fills the page on wider screens (the editor scrolls its panes itself); on a phone the editor
    // stacks and the page scrolls as a whole.
    <div className="flex flex-col md:h-full">
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-surface px-4 py-2">
        <Link
          to={`/projects/${projectId}/sessions/${sessionId}`}
          className="min-w-0 max-w-full truncate text-sm text-muted hover:text-text"
        >
          ← {session.title}
        </Link>
        <span className="text-xs text-muted">
          {autosave === 'pending' || autosave === 'saving'
            ? 'Saving draft…'
            : autosave === 'saved'
              ? 'Draft saved'
              : autosave === 'error'
                ? 'Draft not saved — check your connection'
                : `v${version?.versionNumber ?? ''}`}
        </span>
        <div className="ml-auto flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <Input
            value={summary}
            maxLength={500}
            placeholder="What changed? (optional)"
            onChange={(e) => setSummary(e.target.value)}
            className="w-full sm:w-60"
          />
          {dirty && (
            <Button variant="ghost" onClick={() => void discard()}>
              Discard changes
            </Button>
          )}
          <Button variant="default" disabled={!dirty || saving} onClick={() => void saveVersion()}>
            {saving ? 'Saving…' : 'Save version'}
          </Button>
        </div>
      </div>
      {staleDraft && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-surface-2 px-4 py-2 text-sm">
          <span>
            You have unsaved changes from {relativeTime(staleDraft.updatedAt)}, made before a newer
            version was saved.
          </span>
          <Button
            size="sm"
            onClick={() => {
              handleChange(staleDraft.deck);
              setEditorKey((k) => k + 1);
              setStaleDraft(null);
            }}
          >
            Load them anyway
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setStaleDraft(null)}>
            Ignore
          </Button>
        </div>
      )}
      {error && <p className="border-b border-border px-4 py-2 text-sm text-danger">{error}</p>}
      <div className="p-2 md:min-h-0 md:flex-1 md:p-0">
        <SlideEditor
          key={editorKey}
          deck={deck}
          onChange={handleChange}
          imageUrls={imageUrls}
          onUploadImage={uploadImage}
        />
      </div>
    </div>
  );
}
